import { useEffect, useId, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import {
  enableWarehouseNativeMetricFilters,
  metricEventFiltersQueryBuilder,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { trackMetricsEvent } from '@gonfalon/metrics/analytics';
import { SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY } from '@gonfalon/metrics/constants';
import { EventDataPreview } from '@gonfalon/metrics/EventDataPreview';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { HelpTooltip } from '@gonfalon/ui/HelpTooltip';
import { Button, FieldGroup } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { RequiredLabel } from '../../CreateMetricModal/components/RequiredLabel';
import { TraceField } from '../../CreateMetricModal/components/TraceField';
import type { MetricForm } from '../../CreateMetricModal/formUtils';
import { defaultContextAttributeFilter, defaultEventPropertyFilter } from '../../CreateMetricModal/formUtils';
import { MetricEventFilters } from '../../CreateMetricModal/MetricEventFilters';
import { NumericSpanAttributeField } from '../../CreateMetricModal/MetricEventFilters/NumericSpanAttributeField';
import { canEditWithQueryBuilder } from '../../CreateMetricModal/MetricEventQueryBuilder/eventQueryConversion';

import { EventDefaultPicker } from './components/EventDefaultPicker';
import { EventQueryField } from './components/EventQueryField';
import { MetricConfigRow } from './components/MetricConfigRow';
import { WarehouseEventQueryField } from './components/WarehouseEventQueryField';
import { EventActivityFormField } from './EventActivityFormField';
import { FilterSelectionPopover } from './FilterSelectionPopover';
import { AggregateAsField, MetricDefinitionFormField } from './MetricDefinitionFormField';
import { UnitFormField } from './UnitFormField';
import {
  supportsValueColumn,
  useIsValueColumnOptional,
  VALUE_COLUMN_HELP_TEXT,
  ValueColumnField,
} from './ValueColumnField';
import { WarehouseValueColumnField } from './WarehouseValueColumnField';

import styles from '../../CreateMetricModal/CreateMetricModal.module.css';

interface CustomMetricKindDetailsProps {
  context: UseFormReturn<MetricForm>;
  /** Measurement window and winsorization, rendered at the end of the source configuration. */
  winsorizationAndWindowing?: React.ReactNode;
  /** Arranges the source configuration into sections. Only the create modal opts in so far. */
  useSectionedLayout?: boolean;
}

export const CustomMetricKindDetails: React.FC<CustomMetricKindDetailsProps> = ({
  context,
  winsorizationAndWindowing,
  useSectionedLayout = false,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = context;
  const [eventDefaultValue, measureType, unitAggregationType, analysisType, filters, kind] = useWatch({
    control,
    name: ['eventDefault', 'measureType', 'unitAggregationType', 'analysisType', 'filters', 'kind'],
  });

  const unitsWithoutEvents: 'included' | 'excluded' = eventDefaultValue?.disabled ? 'excluded' : 'included';
  const hideEventDefault = analysisType === 'percentile';
  const eventKey = useWatch({ control, name: 'eventKey' });
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  const projectKey = useProjectKey();
  const { data: dataSourcesData } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const dataSourceIntegrationKey = dataSourcesData?.items?.find(
    (dataSource) => dataSource.key === dataSourceKeyFormValue,
  )?.integrationKey;
  const showWarehouseValueColumnPicker = kind !== 'trace' && supportsValueColumn(dataSourceKeyFormValue);
  // Custom-event metrics use the QueryBuilder path (traces have their own builder). `filters` stays
  // in form state across data-source switches (the EventQueryField Controller no longer unregisters),
  // so this hosted query builder only *renders* for LaunchDarkly-hosted sources — warehouse-native
  // sources get their own column-based editor behind enableWarehouseNativeMetricFilters instead.
  const isQueryBuilderCustomMode = metricEventFiltersQueryBuilder() && kind === 'custom';
  const isEventQueryBuilderEnabled = isQueryBuilderCustomMode && isLDHosted(dataSourceKeyFormValue);

  // Gate the flag-on editor behind a "Filter events" reveal button (mirrors legacy). A metric that
  // already has filters shows the editor immediately; a filter arriving after mount opens it too.
  const [showEventFilters, setShowEventFilters] = useState(filters !== undefined);
  useEffect(() => {
    if (filters !== undefined) {
      setShowEventFilters(true);
    }
  }, [filters]);

  // Flag on: preview always shows (instances API takes only the event key). Flag off: keep main's
  // behavior — hide the preview once filters are set.
  const showEventDataPreview = isEventQueryBuilderEnabled
    ? !errors.eventKey
    : !errors.eventKey && filters === undefined;

  const eventDefaultFieldLabel = useMemo(() => {
    return msg('metrics.metricDefinition.unitsWithoutEventsFieldLabel', { kind: kind ?? 'custom' });
  }, [kind]);

  const showWarehouseEventFilters =
    kind === 'custom' &&
    enableWarehouseNativeMetricFilters() &&
    dataSourceIntegrationKey === SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY;

  const showValueColumnFields = measureType === 'value' && unitAggregationType !== 'count_distinct';
  // The sectioned row owns the label, so it carries the field's required marker as well.
  const isValueColumnOptional = useIsValueColumnOptional(dataSourceKeyFormValue);
  const valueColumnLabelId = useId();
  const spanAttributeLabelId = useId();

  return (
    <>
      {kind === 'trace' ? (
        <TraceField context={context} />
      ) : (
        <div
          className={clsx(styles.eventKeyContainer, {
            [styles.eventKeyContainerQueryBuilder]: isEventQueryBuilderEnabled,
          })}
        >
          {isEventQueryBuilderEnabled ? (
            <>
              {/* Flag on: the "Filter events" button reveals the QueryBuilder editor beside the event
               * key. Fall back to the legacy editor only for a stored filter the bar can't round-trip
               * (canEditWithQueryBuilder false) so editing never drops data. */}
              <div className={styles.eventActivityFiltersRow}>
                <EventActivityFormField context={context} />
                {!showEventFilters && (
                  <Button
                    variant="minimal"
                    className={styles.filterButton}
                    isDisabled={!isLDHosted(dataSourceKeyFormValue)}
                    onPress={() => {
                      setShowEventFilters(true);
                      trackMetricsEvent('Metrics Filter Button Clicked', { eventKey });
                    }}
                  >
                    <span className={styles.filterText}>
                      <Icon name="filter-list" /> <Message id="metrics.eventFilters.filterEvents" />
                    </span>
                  </Button>
                )}
              </div>
              {showEventFilters &&
                (canEditWithQueryBuilder(filters) ? (
                  <EventQueryField context={context} />
                ) : (
                  <MetricEventFilters
                    context={context}
                    headerTitle={msg('metrics.eventFilters.filterEventBy')}
                    showInfoTooltip
                  />
                ))}
            </>
          ) : (
            <div className={styles.eventActivityFiltersRow}>
              {/* Flag off: Legacy filtering. the "Filter event by" button seeds `filters`, revealing <MetricEventFilters> below. */}
              <EventActivityFormField context={context} />
              {filters === undefined && isLDHosted(dataSourceKeyFormValue) && (
                <FilterSelectionPopover
                  onSelectContextKind={() => setValue('filters', defaultContextAttributeFilter)}
                  onSelectEventProperty={() => setValue('filters', defaultEventPropertyFilter)}
                  placement="bottom right"
                >
                  <Button
                    variant="minimal"
                    className={styles.filterButton}
                    onPress={() =>
                      trackMetricsEvent('Metrics Filter Button Clicked', {
                        eventKey,
                      })
                    }
                  >
                    <span className={styles.filterText}>
                      <Icon name="filter-list" /> <Message id="metrics.eventFilters.filterEventBy" />
                    </span>
                  </Button>
                </FilterSelectionPopover>
              )}
            </div>
          )}

          {showEventDataPreview && isLDHosted(dataSourceKeyFormValue) && (
            <EventDataPreview
              eventKey={eventKey}
              eventKind="custom"
              displayEmptyState
              defaultExpanded
              dataSourceKey={dataSourceKeyFormValue}
              eventLimit={5}
            />
          )}
        </div>
      )}

      {showWarehouseEventFilters && <WarehouseEventQueryField context={context} />}

      {/* Trace metrics and the legacy (flag-off) custom path edit `filters` here, and only for
       * LD-hosted sources: warehouse sources either get WarehouseEventQueryField (snowflake-experimentation)
       * or no filter editor at all, since the legacy editor's hosted `data_json.` / context-attribute
       * pickers don't apply to warehouse columns. */}
      {filters !== undefined &&
        !isQueryBuilderCustomMode &&
        !showWarehouseEventFilters &&
        isLDHosted(dataSourceKeyFormValue) && <MetricEventFilters context={context} />}

      {/* Sectioned layout: the aggregation method closes the source configuration, after everything
       * that narrows which events count — the event key and its filters — and before the add-ons.
       * The definition sentence below can then read as static prose. */}
      {useSectionedLayout && <AggregateAsField context={context} />}

      {winsorizationAndWindowing}

      {/* Sectioned layout: what to aggregate on is the first configuration row, below the metric
       * definition. It only appears for value-type aggregations, so above the band it made the
       * whole band jump as the aggregation changed. */}
      <MetricDefinitionFormField
        context={context}
        useSectionedLayout={useSectionedLayout}
        externalAggregationField={useSectionedLayout}
        leadingRows={
          useSectionedLayout && showValueColumnFields ? (
            <>
              {kind === 'trace' && (
                <MetricConfigRow
                  inGrid
                  isRequired
                  label={msg('metrics.metricDefinition.spanAttributeRowLabel')}
                  labelId={spanAttributeLabelId}
                >
                  <div className={styles.aggregateOnField}>
                    <NumericSpanAttributeField control={control} aria-labelledby={spanAttributeLabelId} />
                  </div>
                </MetricConfigRow>
              )}
              {showWarehouseValueColumnPicker && (
                <MetricConfigRow
                  inGrid
                  isRequired={!isValueColumnOptional}
                  label={msg('metrics.metricDefinition.valueColumnLabel')}
                  labelId={valueColumnLabelId}
                  tooltip={<HelpTooltip placement="right">{VALUE_COLUMN_HELP_TEXT}</HelpTooltip>}
                >
                  <div className={styles.aggregateOnField}>
                    <ValueColumnField
                      key={dataSourceKeyFormValue}
                      name="valueColumn"
                      dataSourceKey={dataSourceKeyFormValue}
                      aria-labelledby={valueColumnLabelId}
                    />
                  </div>
                </MetricConfigRow>
              )}
            </>
          ) : undefined
        }
      />
      {!useSectionedLayout && showValueColumnFields && (
        <div className={styles.formElementsGroup}>
          {kind === 'trace' && <NumericSpanAttributeField control={control} />}
          {showWarehouseValueColumnPicker && <WarehouseValueColumnField dataSourceKey={dataSourceKeyFormValue} />}
          <UnitFormField context={context} />
          {!hideEventDefault && (
            <FieldGroup errorMessage={errors.eventDefault?.message} className={styles.labelInputWrapper}>
              <RequiredLabel label={eventDefaultFieldLabel} htmlFor="eventDefault" />
              <EventDefaultPicker
                unitsWithoutEvents={unitsWithoutEvents}
                setValue={setValue}
                className={styles.eventDefaultPicker}
                metricKind={kind}
              />
            </FieldGroup>
          )}
        </div>
      )}
    </>
  );
};
